/** As tramas do corte no papel (SVG e PDF das Vistas 2D).
 *
 *  O 3D e as Vistas 2D usam os mesmos estilos do corte (base e regras
 *  por classe ou material, viewer/section-box/estilosDeCorte.ts): o que
 *  se vê cortado no 3D é o que sai na planta. Aqui só se desenha a
 *  trama em unidades de papel.
 *
 *  Convenção da diagonal: "/" (45 graus, como o ANSI31 dos CAD), igual
 *  no 3D, no SVG e no PDF; a inversa é "\\".
 *
 *  Puro. */

import type { EstiloDeCorte, Trama } from '@viewer/section-box/estilosDeCorte'

/** O estilo do corte de um elemento no papel; null = poché da camada,
 *  como antes. */
export type EstiloDoCorteNoPapel = (localId: number | undefined, camada: string) => EstiloDeCorte | null

/** Espaçamento da trama no papel, em mm. */
export const ESPACAMENTO_MM = 1.5
/** Espessura do traço da trama no papel, em mm. */
export const TRACO_DA_TRAMA_MM = 0.1

/** Uma chave curta e segura para ids de SVG. */
export function idDoPadrao(e: EstiloDeCorte): string {
  return `trama-${e.trama}-${e.fundo.slice(1)}-${e.corDaTrama.slice(1)}`
}

/** Pontos fixos ao acaso para o betão, numa célula de 3 x 3 espaçamentos
 *  (sempre os mesmos, para o desenho não mudar de exportação para
 *  exportação). [x, y, raio] em frações da célula. */
const PONTOS_DO_BETAO: ReadonlyArray<[number, number, number]> = [
  [0.12, 0.18, 0.05], [0.47, 0.09, 0.03], [0.81, 0.27, 0.06], [0.28, 0.52, 0.03],
  [0.63, 0.58, 0.05], [0.9, 0.71, 0.03], [0.17, 0.86, 0.04], [0.52, 0.9, 0.06],
]

/** O <pattern> SVG de um estilo (null para "cheio": basta a cor). */
export function padraoSvg(e: EstiloDeCorte, esc: (s: string) => string): string | null {
  if (e.trama === 'cheio') return null
  const s = ESPACAMENTO_MM
  const w = TRACO_DA_TRAMA_MM
  const c = esc(e.corDaTrama)
  const f = esc(e.fundo)
  const id = idDoPadrao(e)
  const linha = (rot: number, dupla = false) =>
    `<pattern id="${id}" patternUnits="userSpaceOnUse" width="${s}" height="${s}" patternTransform="rotate(${rot})">` +
    `<rect width="${s}" height="${s}" fill="${f}"/>` +
    `<line x1="${s / 2}" y1="0" x2="${s / 2}" y2="${s}" stroke="${c}" stroke-width="${w}"/>` +
    (dupla ? `<line x1="0" y1="${s / 2}" x2="${s}" y2="${s / 2}" stroke="${c}" stroke-width="${w}"/>` : '') +
    '</pattern>'
  switch (e.trama) {
    case 'diagonal':
      return linha(45)
    case 'diagonal-inversa':
      return linha(-45)
    case 'cruzada':
      return linha(45, true)
    case 'vertical':
      return linha(0)
    case 'horizontal':
      return linha(90)
    case 'pontos':
      return (
        `<pattern id="${id}" patternUnits="userSpaceOnUse" width="${s}" height="${s}">` +
        `<rect width="${s}" height="${s}" fill="${f}"/><circle cx="${s / 2}" cy="${s / 2}" r="0.16" fill="${c}"/></pattern>`
      )
    case 'betao': {
      const t = s * 3
      const pts = PONTOS_DO_BETAO.map(([x, y, r]) => `<circle cx="${(x * t).toFixed(3)}" cy="${(y * t).toFixed(3)}" r="${(r * t).toFixed(3)}" fill="${c}"/>`).join('')
      return `<pattern id="${id}" patternUnits="userSpaceOnUse" width="${t}" height="${t}"><rect width="${t}" height="${t}" fill="${f}"/>${pts}</pattern>`
    }
    case 'isolamento': {
      const lw = s * 2
      const h = s * 1.4
      return (
        `<pattern id="${id}" patternUnits="userSpaceOnUse" width="${lw}" height="${h}"><rect width="${lw}" height="${h}" fill="${f}"/>` +
        `<path d="M0 ${h / 2} Q ${lw / 4} 0 ${lw / 2} ${h / 2} T ${lw} ${h / 2}" fill="none" stroke="${c}" stroke-width="${w}"/></pattern>`
      )
    }
    default:
      return null
  }
}

export interface TramaNaCaixa {
  /** Segmentos [x1, y1, x2, y2]. */
  linhas: Array<[number, number, number, number]>
  /** Pontos [x, y, raio]. */
  pontos: Array<[number, number, number]>
}

/**
 * A trama a cobrir uma caixa, em segmentos e pontos, para quem não tem
 * padrões (o PDF: desenha-se por dentro de um recorte do contorno).
 * Unidades as da caixa; `esp` o espaçamento nessas unidades. Com o y
 * para cima (PDF), a diagonal sai "/".
 */
export function tramaNaCaixa(
  trama: Trama,
  caixa: { x0: number; y0: number; x1: number; y1: number },
  esp: number,
): TramaNaCaixa {
  const linhas: TramaNaCaixa['linhas'] = []
  const pontos: TramaNaCaixa['pontos'] = []
  const { x0, y0, x1, y1 } = caixa
  if (!(esp > 0) || !(x1 > x0) || !(y1 > y0)) return { linhas, pontos }
  const diag = Math.hypot(x1 - x0, y1 - y0)
  // Nunca mais do que isto por elemento: uma laje enorme a uma escala
  // pequena não pode encher o PDF de milhões de traços.
  const MAX = 4000
  const paralelas = (graus: number) => {
    const a = (graus * Math.PI) / 180
    const d = [Math.cos(a), Math.sin(a)]
    const n = [-d[1], d[0]]
    const proj = [
      [x0, y0], [x1, y0], [x0, y1], [x1, y1],
    ].map(([x, y]) => x * n[0] + y * n[1])
    const lo = Math.ceil(Math.min(...proj) / esp) * esp
    const hi = Math.max(...proj)
    const cx = (x0 + x1) / 2
    const cy = (y0 + y1) / 2
    const base = cx * n[0] + cy * n[1]
    for (let t = lo; t <= hi && linhas.length < MAX; t += esp) {
      const mx = cx + n[0] * (t - base)
      const my = cy + n[1] * (t - base)
      linhas.push([mx - d[0] * diag, my - d[1] * diag, mx + d[0] * diag, my + d[1] * diag])
    }
  }
  switch (trama) {
    case 'diagonal':
      paralelas(45)
      break
    case 'diagonal-inversa':
      paralelas(-45)
      break
    case 'cruzada':
      paralelas(45)
      paralelas(-45)
      break
    case 'horizontal':
      paralelas(0)
      break
    case 'vertical':
      paralelas(90)
      break
    case 'pontos':
      for (let x = Math.ceil(x0 / esp) * esp; x <= x1; x += esp) {
        for (let y = Math.ceil(y0 / esp) * esp; y <= y1 && pontos.length < MAX; y += esp) pontos.push([x, y, esp * 0.11])
      }
      break
    case 'betao': {
      const t = esp * 3
      for (let x = Math.floor(x0 / t) * t; x <= x1; x += t) {
        for (let y = Math.floor(y0 / t) * t; y <= y1 && pontos.length < MAX; y += t) {
          for (const [fx, fy, fr] of PONTOS_DO_BETAO) pontos.push([x + fx * t, y + fy * t, fr * t])
        }
      }
      break
    }
    case 'isolamento': {
      // Ondas aproximadas por um ziguezague suave (8 segmentos por onda).
      const lw = esp * 2
      const h = esp * 1.4
      for (let y = Math.floor(y0 / h) * h; y <= y1 && linhas.length < MAX; y += h) {
        let px = Math.floor(x0 / lw) * lw
        let py = y + h / 2
        for (let x = px; x <= x1 + lw; x += lw / 8) {
          const ny = y + h / 2 + Math.sin(((x % lw) / lw) * Math.PI * 2) * h * 0.35
          if (x > px) linhas.push([px, py, x, ny])
          px = x
          py = ny
        }
      }
      break
    }
    default:
      break
  }
  return { linhas, pontos }
}
